/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

/**
 * Persists optional enterprise attribution from the URL so sign-in requests
 * (Google OAuth callback, email code) can attach enterprise_canonical_id and
 * optional additional_info.
 *
 * Supported deep-link formats:
 *   1. Legacy: ?enterprise_source=<json payload>
 *      where payload includes { source, user_token, ts, exp }.
 *   2. Flat params:
 *      ?enterprise_source=newsbreak&user_token=...&ts=...&exp=...
 */
const STORAGE_KEY = "povchat_enterprise_canonical_id_v1";
const STORAGE_INFO_KEY = "povchat_enterprise_additional_info_v1";
const STORAGE_PAYLOAD_KEY = "povchat_enterprise_payload_v1";
const STORAGE_SEEN_KEY = "povchat_enterprise_source_seen_v1";

type EnterpriseSourcePayload = {
  source?: string;
  user_token?: string;
  ts?: number | string;
  exp?: number | string;
};

function parseEnterpriseSource(raw: string): {
  enterpriseCanonicalId: string;
  enterprisePayload?: EnterpriseSourcePayload;
  additionalInfo?: string;
  enterpriseUserToken?: string;
} | null {
  const value = raw.trim();
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as EnterpriseSourcePayload;
    const source = parsed?.source?.trim();
    if (!source) return null;

    const userToken = parsed?.user_token?.trim();
    const payload: EnterpriseSourcePayload = {
      ...(source ? { source } : {}),
      ...(userToken ? { user_token: userToken } : {}),
      ...(parsed?.ts != null ? { ts: parsed.ts } : {}),
      ...(parsed?.exp != null ? { exp: parsed.exp } : {}),
    };
    const additionalInfo =
      Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined;
    return {
      enterpriseCanonicalId: source,
      enterprisePayload: payload,
      additionalInfo,
      enterpriseUserToken: userToken,
    };
  } catch {
    return null;
  }
}

function parseIntegerParam(value: string | null): number | undefined {
  if (value == null) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function persistEnterpriseSourceFromSearch(search: string): void {
  const params = new URLSearchParams(search);
  const raw = params.get("enterprise_source");
  if (!raw) return;

  sessionStorage.setItem(STORAGE_SEEN_KEY, "1");

  let parsed = parseEnterpriseSource(raw);
  if (!parsed) {
    const source = raw.trim();
    if (!source) return;

    const userToken = params.get("user_token")?.trim() || undefined;
    const ts = parseIntegerParam(params.get("ts"));
    const exp = parseIntegerParam(params.get("exp"));
    const payload: EnterpriseSourcePayload = {
      source,
      ...(userToken ? { user_token: userToken } : {}),
      ...(ts != null ? { ts } : {}),
      ...(exp != null ? { exp } : {}),
    };

    parsed = {
      enterpriseCanonicalId: source,
      enterprisePayload: payload,
      additionalInfo: JSON.stringify(payload),
      enterpriseUserToken: userToken,
    };
  }

  sessionStorage.setItem(STORAGE_KEY, parsed.enterpriseCanonicalId);
  if (parsed.additionalInfo) {
    sessionStorage.setItem(STORAGE_INFO_KEY, parsed.additionalInfo);
  } else {
    sessionStorage.removeItem(STORAGE_INFO_KEY);
  }
  if (parsed.enterprisePayload && Object.keys(parsed.enterprisePayload).length) {
    sessionStorage.setItem(
      STORAGE_PAYLOAD_KEY,
      JSON.stringify(parsed.enterprisePayload),
    );
  } else {
    sessionStorage.removeItem(STORAGE_PAYLOAD_KEY);
  }
}

export function hasEnterpriseAuthContext(search = ""): boolean {
  const params = new URLSearchParams(search);
  if (params.has("enterprise_source")) return true;

  try {
    return Boolean(
      sessionStorage.getItem(STORAGE_SEEN_KEY)?.trim() ||
        sessionStorage.getItem(STORAGE_KEY)?.trim(),
    );
  } catch {
    return false;
  }
}

/** JSON fields to merge into auth POST bodies when a canonical id was captured. */
export function enterpriseAuthJsonExtras(): Record<string, unknown> {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY)?.trim();
    if (v) {
      const additionalInfo = sessionStorage.getItem(STORAGE_INFO_KEY)?.trim();
      const payloadRaw = sessionStorage.getItem(STORAGE_PAYLOAD_KEY)?.trim();
      let payload: EnterpriseSourcePayload | undefined;
      if (payloadRaw) {
        try {
          payload = JSON.parse(payloadRaw) as EnterpriseSourcePayload;
        } catch {
          payload = undefined;
        }
      }

      return {
        enterprise_canonical_id: v,
        ...(additionalInfo ? { additional_info: additionalInfo } : {}),
        ...(payload ? { payload } : {}),
        ...(payload?.user_token ? { user_token: payload.user_token } : {}),
      };
    }
  } catch {
    /* sessionStorage unavailable */
  }
  return {};
}
